import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { treatmentApi } from 'src/api/treatment';
import Iconify from 'src/components/iconify';
import { CreateTreatmentDto, UpdateTreatmentDto } from 'woofwoof-api';
import ProfileTreatmentDeleteModal from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentDeleteModal';
import ProfileTreatmentsTableSkeleton from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentsTableSkeleton';
import OrganizationTreatmentCatalogFormModal, {
  CatalogFormSubmitValues,
} from './OrganizationTreatmentCatalogFormModal';
import OrganizationTreatmentsCatalogTable from './OrganizationTreatmentsCatalogTable';
import { translateKey } from 'src/locales/translate';


export function OrganizationTreatmentsContent() {
  const { organizationId } = useParams<{ organizationId: string }>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [treatmentId, setTreatmentId] = useState('');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleOnRowClick = (id: string) => {
    setTreatmentId(id);
    setIsModalOpen(true);
  };

  const handleOpenModal = () => {
    setTreatmentId('');
    setIsModalOpen(true);
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setTreatmentId('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const { data, isLoading } = useQuery(
    ['organization-treatments', organizationId],
    () =>
      treatmentApi.treatmentControllerGetOrganizationTreatments({
        organizationId: organizationId!,
      }),
    { retry: 1, enabled: Boolean(organizationId) },
  );

  const { mutate: createTreatment, isLoading: isCreating } = useMutation({
    mutationFn: (payload: CreateTreatmentDto) =>
      treatmentApi.treatmentControllerCreateTreatment({
        createTreatmentDto: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'organization-treatments',
        organizationId,
      ]);
      enqueueSnackbar(translateKey("snackbar.treatmentCatalogAdded"), {
        variant: 'success',
      });
      setIsModalOpen(false);
    },
    onError: () => {
      enqueueSnackbar(translateKey("snackbar.treatmentAddError"), {
        variant: 'error',
      });
    },
  });

  const { mutate: updateTreatment, isLoading: isUpdating } = useMutation({
    mutationFn: (payload: UpdateTreatmentDto) =>
      treatmentApi.treatmentControllerUpdateTreatment({
        treatmentId,
        updateTreatmentDto: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'organization-treatments',
        organizationId,
      ]);
      enqueueSnackbar(translateKey("snackbar.treatmentUpdated"), { variant: 'success' });
      setIsModalOpen(false);
      setTimeout(() => setTreatmentId(''), 500);
    },
    onError: () => {
      enqueueSnackbar(translateKey("snackbar.treatmentUpdateError"), {
        variant: 'error',
      });
    },
  });

  const { mutate: deleteTreatment, isLoading: isDeleting } = useMutation({
    mutationFn: () =>
      treatmentApi.treatmentControllerDeleteTreatment({ treatmentId }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'organization-treatments',
        organizationId,
      ]);
      enqueueSnackbar(translateKey("snackbar.treatmentDeleted"), { variant: 'success' });
      setIsModalOpen(false);
      setIsDeleteModalOpen(false);
      setTimeout(() => setTreatmentId(''), 500);
    },
    onError: () => {
      enqueueSnackbar(translateKey("snackbar.treatmentDeleteError"), {
        variant: 'error',
      });
    },
  });

  const selectedTreatment = treatmentId
    ? data?.data.find((t: any) => t.id === treatmentId)
    : undefined;

  const handleOnSubmit = useCallback(
    (values: CatalogFormSubmitValues) => {
      if (treatmentId) {
        updateTreatment(values as UpdateTreatmentDto);
      } else {
        createTreatment(values as CreateTreatmentDto);
      }
    },
    [createTreatment, treatmentId, updateTreatment],
  );

  return (
    <>
      <Stack p={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {isLoading ? (
            <Skeleton variant="rounded" height="28px" width="120px" />
          ) : (
            <Typography variant="h6">{translateKey("hardcoded.katalog_zabiegow")}</Typography>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" height="36px" width="140px" />
          ) : (
            <Button
              size="medium"
              startIcon={<Iconify icon="eva:plus-fill" />}
              variant="contained"
              color="primary"
              data-testid="catalog-add-open"
              onClick={handleOpenModal}
            >{translateKey("common.add")}</Button>
          )}
        </Stack>
      </Stack>

      {isLoading ? (
        <ProfileTreatmentsTableSkeleton />
      ) : (
        <OrganizationTreatmentsCatalogTable
          data={(data?.data as any) ?? []}
          onRowClick={handleOnRowClick}
        />
      )}

      <OrganizationTreatmentCatalogFormModal
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={handleOpenDeleteModal}
        treatment={selectedTreatment as any}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />

      <ProfileTreatmentDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        onDelete={deleteTreatment}
        isLoading={isDeleting}
      />
    </>
  );
}
